import { Node, mergeAttributes } from '@tiptap/core';

/**
 * HearEntry: the HEAR journal itself, inside a lesson.
 *
 * The course teaches a journaling method across five sessions and never gives
 * the learner the journal — every session ends in a free-text box, and Session 5
 * asks them to plan a lifelong habit they have never actually performed here.
 *
 * This writes a real entry, not a demo: it posts to /progress/personal-devotion,
 * the endpoint that already backs standalone HEAR entries, so what a learner
 * writes in the lesson lands in the same journal as everything they write later
 * in a reading plan. No new table, no throwaway copy.
 *
 * Attrs:
 *  - reference: the passage the entry is about (also the entry's title)
 *  - intro: one line above the fields
 */
const FIELDS = [
  { key: 'highlight', marker: 'H', label: 'Highlight', hint: 'What verse or phrase stands out?' },
  { key: 'explain', marker: 'E', label: 'Explain', hint: 'What does it mean in context?' },
  { key: 'apply', marker: 'A', label: 'Apply', hint: 'How does this apply to your life?' },
  { key: 'respond', marker: 'R', label: 'Respond', hint: 'Write a prayer back to God.' },
];

export const HearEntry = Node.create({
  name: 'hearEntry',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      reference: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-reference') || '',
        renderHTML: (a) => ({ 'data-reference': a.reference || '' }),
      },
      intro: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-intro') || '',
        renderHTML: (a) => ({ 'data-intro': a.intro || '' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="hear-entry"]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'hear-entry', class: 'hear-entry' }),
      ['div', { class: 'hear-entry-eyebrow' }, 'HEAR journal'],
      ...(node.attrs.reference ? [['div', { class: 'hear-entry-ref' }, node.attrs.reference]] : []),
      ...(node.attrs.intro ? [['div', { class: 'hear-entry-intro' }, node.attrs.intro]] : []),
      ...FIELDS.map((f) => [
        'div',
        { class: 'hear-entry-field' },
        ['label', { class: 'hear-entry-label' }, [
          'span', { class: 'hear-entry-marker' }, f.marker,
        ], ' ' + f.label],
        ['textarea', { class: 'hear-entry-input', 'data-field': f.key, rows: '3', placeholder: f.hint }],
      ]),
      ['div', { class: 'hear-entry-actions' },
        ['button', { type: 'button', class: 'hear-entry-save' }, 'Save to my journal'],
        ['span', { class: 'hear-entry-status' }],
      ],
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'hear-entry';
      dom.setAttribute('data-type', 'hear-entry');
      dom.setAttribute('data-reference', node.attrs.reference || '');

      const eyebrow = document.createElement('div');
      eyebrow.className = 'hear-entry-eyebrow';
      eyebrow.textContent = 'HEAR journal';
      dom.appendChild(eyebrow);

      if (node.attrs.reference) {
        const ref = document.createElement('div');
        ref.className = 'hear-entry-ref';
        ref.textContent = node.attrs.reference;
        dom.appendChild(ref);
      }
      if (node.attrs.intro) {
        const intro = document.createElement('div');
        intro.className = 'hear-entry-intro';
        intro.textContent = node.attrs.intro;
        dom.appendChild(intro);
      }

      FIELDS.forEach((f) => {
        const wrap = document.createElement('div');
        wrap.className = 'hear-entry-field';
        const label = document.createElement('label');
        label.className = 'hear-entry-label';
        const marker = document.createElement('span');
        marker.className = 'hear-entry-marker';
        marker.textContent = f.marker;
        label.appendChild(marker);
        label.appendChild(document.createTextNode(' ' + f.label));
        const ta = document.createElement('textarea');
        ta.className = 'hear-entry-input';
        ta.setAttribute('data-field', f.key);
        ta.rows = 3;
        ta.placeholder = f.hint;
        wrap.appendChild(label);
        wrap.appendChild(ta);
        dom.appendChild(wrap);
      });

      const actions = document.createElement('div');
      actions.className = 'hear-entry-actions';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hear-entry-save';
      btn.textContent = 'Save to my journal';
      const status = document.createElement('span');
      status.className = 'hear-entry-status';
      actions.appendChild(btn);
      actions.appendChild(status);
      dom.appendChild(actions);

      return { dom };
    };
  },
});

export default HearEntry;
